import React from "react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Dna, Microscope, Activity, BrainCircuit } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const breakdownItems = [{
  id: "item-1",
  icon: <Dna className="size-5 text-primary" />,
  question: "How does genetic testing improve psychiatric care?",
  answer: "Traditional psychiatry often relies on a 'trial and error' approach to medication. By analyzing your unique genetic markers, we can predict how your body will metabolize specific drugs. This allows Dr. Sharmat and Maryellen Stephens to select medications that are more likely to be effective and less likely to cause adverse side effects from day one."
}, {
  id: "item-2",
  icon: <Microscope className="size-5 text-primary" />,
  question: "What specific insights does the evaluation provide?",
  answer: "Our comprehensive panel looks at key metabolic pathways (such as the CYP450 enzymes) and neurotransmitter receptors. We identify whether you are a rapid, normal, or poor metabolizer of certain compounds. This data explains why past medications may have failed or caused toxicity, providing a clear roadmap for future treatment."
}, {
  id: "item-3",
  icon: <Activity className="size-5 text-primary" />,
  question: "Is this approach suitable for complex or resistant cases?",
  answer: "Absolutely. The genetic advantage is most profound for patients who have not found relief through standard treatments. By uncovering the biological drivers behind your resistance to medication, we can engineer a sophisticated, evidence-based regimen tailored specifically to your physiology."
}, {
  id: "item-4",
  icon: <BrainCircuit className="size-5 text-primary" />,
  question: "How is the testing process administered?",
  answer: "The process is non-invasive and simple. It typically involves a quick cheek swab that can be done in our New York City office or sent to your home if you are a remote patient. Once the lab analyzes your sample, our clinical team integrates the findings into your personalized medication management plan."
}];
export default function TheGeneticAdvantageBreakdown() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="the-genetic-advantage-breakdown" ref={ref} className={`dark bg-background text-foreground py-24 relative overflow-hidden transition-opacity duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
      {}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent/5 rounded-full blur-[100px] pointer-events-none" />

      <div className="container mx-auto px-6 relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          {}
          <div className="lg:col-span-5 space-y-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium tracking-wide">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
              </span>
              Precision Medicine
            </div>

            <h2 className="text-4xl md:text-5xl font-serif leading-tight text-foreground">
              The Genetic <br />
              <span className="text-primary">Advantage</span>
            </h2>

            <p className="text-lg text-muted-foreground leading-relaxed">
              At Sharmat Psychiatric Group, we bridge the gap between advanced
              biotech and compassionate care. Our methodology replaces guesswork
              with hard data, ensuring your treatment is as unique as your DNA.
            </p>

            <div className="pt-4">
              <Button asChild size="lg" className="rounded-full text-base px-8">
                <Link to="/PrecisionCarePage">Explore Our Methodology</Link>
              </Button>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <div className="bg-card/30 backdrop-blur-md border border-white/5 rounded-2xl p-6 md:p-8 shadow-2xl">
              <Accordion type="single" collapsible className="w-full space-y-4">
                {breakdownItems.map((item, i) => <AccordionItem key={item.id} value={item.id} className="border-b border-white/10 last:border-0 px-2" data-index={i}>
                    <AccordionTrigger className="text-lg md:text-xl font-medium text-foreground hover:text-primary hover:no-underline py-6 transition-all duration-300 hover:bg-white/5 rounded-lg px-4 group">
                      <div className="flex items-center gap-4 text-left">
                        <div className="p-2 rounded-md bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                          {item.icon}
                        </div>
                        <span>{item.question}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="text-lg text-muted-foreground leading-relaxed px-4 pb-6 pl-[4.5rem]">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </div>
          </div>
        </div>
      </div>
    </section>;
}